import React from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "../Icon";
import { TECH_SERVICES } from "../content";
import { useWorks } from "../useWorks";
import { CtaBand, FaqList, PageHero, SectionHead, useSeo } from "../ui";
import NotFound from "./NotFound";

/* 개발 분야별 서비스 — /services/pcb-design, /services/firmware …
   내용은 content.js 의 TECH_SERVICES, 관련 사례는 /api/works 에서 id 로 고른다 */

function RelatedWorks({ ids }) {
  const { items } = useWorks();
  const list = ids.map((id) => items.find((w) => w.id === id)).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <section className="section">
      <SectionHead kicker="WORKS" title="관련 개발 사례" />
      <div className="svc-cards">
        {list.map((w) => (
          <Link key={w.id} to={`/works#${w.id}`} className="svc-card">
            <p className="svc-card__desc">{w.field}</p>
            <h3 className="svc-card__title">{w.title}</h3>
            <p className="svc-card__text">{w.summary}</p>
            <span className="card__more">
              사례 보기 <Icon.ArrowRight width="14" height="14" aria-hidden="true" focusable="false" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function TechService() {
  const { slug } = useParams();
  const s = TECH_SERVICES.find((t) => t.slug === slug);

  /* 없는 분야면 NotFound 가 noindex 를 거는데, 부모 effect 가 나중에 돌아 지우지 않게 같이 건다 */
  useSeo(s ? { title: s.seoTitle, description: s.seoDesc, path: `/services/${s.slug}` } : { noindex: true });

  if (!s) return <NotFound />;

  const contactTo = `/contact?type=${encodeURIComponent(s.inquiryType)}`;
  const others = TECH_SERVICES.filter((t) => t.slug !== s.slug);

  return (
    <>
      <PageHero kicker={s.label} title={s.headline} desc={s.lead}>
        <div className="hero__actions">
          <Link to={contactTo} className="btn btn--primary">
            {s.label} 문의 <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />
          </Link>
          <Link to="/works" className="btn btn--ghost">개발 사례 보기</Link>
        </div>
      </PageHero>

      <section className="section section--tight">
        <SectionHead kicker="WHAT WE DO" title="이런 일을 합니다" />
        <ul className="checklist checklist--row">
          {s.items.map((t) => (
            <li key={t}>
              <Icon.Check width="15" height="15" aria-hidden="true" focusable="false" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="section section--tight">
        <SectionHead kicker="TECH" title="다루는 기술" />
        <ul className="roles__list who-chips">
          {s.tech.map((t) => <li key={t}>{t}</li>)}
        </ul>
        {s.links && (
          <p className="note-line">
            {s.links.map((l, i) => (
              <React.Fragment key={l.to}>
                {i > 0 && " · "}
                <Link to={l.to}>{l.label}</Link>
              </React.Fragment>
            ))}
          </p>
        )}
      </section>

      <RelatedWorks ids={s.works} />

      <section className="section">
        <SectionHead kicker="FAQ" title="자주 묻는 질문" />
        <FaqList items={s.faq} />
      </section>

      <section className="section section--tight">
        <SectionHead kicker="MORE" title="다른 개발 분야" />
        <ul className="tech-grid tech-grid--4">
          {others.map((t) => {
            const I = Icon[t.icon];
            return (
              <li key={t.slug} className="tech-grid__item">
                <span className="tech-grid__icon">
                  {I ? <I width="18" height="18" aria-hidden="true" focusable="false" /> : null}
                </span>
                <Link to={`/services/${t.slug}`}>{t.label}</Link>
              </li>
            );
          })}
        </ul>
      </section>

      <CtaBand title={s.headline} to={contactTo} label={`${s.label} 문의`} />
    </>
  );
}
